import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle2, AlertCircle, Calendar, User, Clock, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client directly for this component
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

interface FeedbackFormField {
  id: string;
  business_id: string;
  field_key: string;
  field_label: string;
  field_type: 'rating' | 'text';
  is_required: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BusinessGoogleReviewSettings {
  google_reviews_enabled: boolean;
  google_review_condition: number;
  google_review_url: string;
}

interface BookingFeedback {
  id: string;
  booking_id: string;
  business_id: string;
  token: string;
  customer_name?: string;
  customer_email?: string;
  overall_quality_rating: number;
  overall_experience_rating: number;
  improvement_suggestions?: string;
  submitted_at: string;
  created_at: string;
  expires_at: string;
  status: 'pending' | 'submitted' | 'expired';
  booking?: {
    id: string;
    booking_number: string;
    title: string;
    start_time: string;
    end_time: string;
    customer?: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
    };
    resources?: Array<{
      resource: {
        id: string;
        first_name: string;
        last_name: string;
        avatar_url?: string;
      };
    }>;
  };
}

interface DynamicFeedbackResponses {
  [fieldKey: string]: string | number;
}

// Self-contained feedback functions
async function getFeedbackByToken(token: string): Promise<BookingFeedback | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    const { data: feedback, error } = await supabase
      .from('booking_feedback')
      .select(`
        *,
        booking:bookings(
          id,
          booking_number,
          title,
          start_time,
          end_time,
          customer:customers(first_name, last_name, email, phone),
          resources:booking_resources(
            resource:resources(id, first_name, last_name, avatar_url)
          )
        )
      `)
      .eq('token', token)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error || !feedback) {
      return null;
    }

    return feedback;
  } catch (error) {
    console.error('Error fetching feedback by token:', error);
    return null;
  }
}

async function getFeedbackFormFields(businessId: string): Promise<FeedbackFormField[]> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('feedback_form_fields')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching feedback form fields:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching feedback form fields:', error);
    return [];
  }
}

async function getBusinessGoogleReviewSettings(businessId: string): Promise<BusinessGoogleReviewSettings | null> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('businesses')
      .select('google_reviews_enabled, google_review_condition, google_review_url')
      .eq('id', businessId)
      .single();

    if (error) {
      console.error('Error fetching business Google review settings:', error);
      return null;
    }

    return data || null;
  } catch (error) {
    console.error('Error fetching business Google review settings:', error);
    return null;
  }
}

async function updateGoogleReviewTracking(token: string, action: 'prompted' | 'clicked'): Promise<void> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return;
  }

  try {
    const updateData: any = {};
    
    if (action === 'prompted') {
      updateData.google_review_prompted = true;
      updateData.google_review_prompted_at = new Date().toISOString();
    } else if (action === 'clicked') {
      updateData.google_review_clicked = true;
      updateData.google_review_clicked_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('booking_feedback')
      .update(updateData)
      .eq('token', token);

    if (error) {
      console.error('Error updating Google review tracking:', error);
    }

    // If this is a click action, also track it as an analytics event
    if (action === 'clicked') {
      try {
        // Get the business ID from the feedback token
        const { data: feedbackData } = await supabase
          .from('booking_feedback')
          .select('business_id, booking_id')
          .eq('token', token)
          .single();

        if (feedbackData?.business_id) {
          // Track as analytics event
          const { error: analyticsError } = await supabase
            .from('analytics_events')
            .insert({
              business_id: feedbackData.business_id,
              event_type: 'google_review_clicked',
              event_data: {
                feedback_token: token,
                booking_id: feedbackData.booking_id,
                source: 'feedback_form'
              },
              page_url: window.location.href,
              user_agent: navigator.userAgent,
              timestamp: new Date().toISOString()
            });

          if (analyticsError) {
            console.error('Error tracking Google review analytics event:', analyticsError);
          } else {
            console.log('✅ Google review click tracked in analytics');
          }
        }
      } catch (analyticsError) {
        console.error('Error tracking Google review analytics event:', analyticsError);
      }
    }
  } catch (error) {
    console.error('Error updating Google review tracking:', error);
  }
}

async function submitDynamicFeedback(token: string, responses: DynamicFeedbackResponses): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  try {
    // Update the feedback with the dynamic responses
    const { error } = await supabase
      .from('booking_feedback')
      .update({
        form_responses: responses,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        form_version: 1,
        // Still populate legacy columns for backward compatibility
        overall_quality_rating: responses.overall_quality ? Number(responses.overall_quality) : null,
        overall_experience_rating: responses.overall_experience ? Number(responses.overall_experience) : null,
        improvement_suggestions: responses.improvement_suggestions ? String(responses.improvement_suggestions) : null
      })
      .eq('token', token)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString());

    if (error) {
      console.error('Error submitting feedback:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    return false;
  }
}

export default function FeedbackPage() {
  const { token } = useParams<{ token: string }>();
  
  const [feedback, setFeedback] = useState<BookingFeedback | null>(null);
  const [formFields, setFormFields] = useState<FeedbackFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Step-based state
  const [currentStep, setCurrentStep] = useState(1);
  const [responses, setResponses] = useState<DynamicFeedbackResponses>({});

  // Google Reviews state
  const [googleReviewSettings, setGoogleReviewSettings] = useState<BusinessGoogleReviewSettings | null>(null);
  const [showGoogleReview, setShowGoogleReview] = useState(false);
  const [googleReviewTracked, setGoogleReviewTracked] = useState(false);

  useEffect(() => {
    const loadFeedback = async () => {
      if (!token) {
        setError('Invalid feedback link');
        setLoading(false);
        return;
      }

      if (!supabase) {
        setError('Application configuration error');
        setLoading(false);
        return;
      }

      try {
        const feedbackData = await getFeedbackByToken(token);
        
        if (feedbackData) {
          setFeedback(feedbackData);
          
          // Load the dynamic form fields for this business
          const fields = await getFeedbackFormFields(feedbackData.business_id);
          setFormFields(fields);
          
          // Load Google review settings for this business
          const googleSettings = await getBusinessGoogleReviewSettings(feedbackData.business_id);
          setGoogleReviewSettings(googleSettings);
          
          // Initialize responses object
          const initialResponses: DynamicFeedbackResponses = {};
          fields.forEach(field => {
            initialResponses[field.field_key] = field.field_type === 'rating' ? 0 : '';
          });
          setResponses(initialResponses);
        } else {
          setError('Feedback link is invalid or has expired');
        }
      } catch (err) {
        console.error('Error loading feedback:', err);
        setError('Failed to load feedback form');
      } finally {
        setLoading(false);
      }
    };

    loadFeedback();
  }, [token]);

  // Check if submitted ratings meet Google review condition
  const checkGoogleReviewEligibility = (): boolean => {
    if (!googleReviewSettings?.google_reviews_enabled || !googleReviewSettings?.google_review_url) {
      return false;
    }

    const condition = googleReviewSettings.google_review_condition;
    const ratingFields = formFields.filter(field => field.field_type === 'rating');

    // Check if any rating meets or exceeds the condition
    for (const field of ratingFields) {
      const rating = responses[field.field_key] as number;
      if (rating && rating >= condition) {
        return true;
      }
    }

    return false;
  };

  // Handle Google review click
  const handleGoogleReviewClick = async () => {
    console.log('🔍 Google review button clicked');
    console.log('🔍 Token:', token);
    console.log('🔍 Google review settings:', googleReviewSettings);
    
    if (!token) {
      console.error('❌ No token available');
      alert('Error: Unable to track review click - no token available');
      return;
    }
    
    if (!googleReviewSettings?.google_review_url) {
      console.error('❌ No Google review URL configured');
      alert('Error: No Google review URL has been configured for this business');
      return;
    }
    
    const reviewUrl = googleReviewSettings.google_review_url.trim();
    console.log('🔗 Opening Google review URL:', reviewUrl);
    
    // Validate URL format
    if (!reviewUrl.startsWith('http://') && !reviewUrl.startsWith('https://')) {
      console.error('❌ Invalid URL format:', reviewUrl);
      alert('Error: Invalid Google review URL format. Please contact support.');
      return;
    }

    try {
    // Track that the link was clicked
      console.log('📊 Tracking Google review click...');
    await updateGoogleReviewTracking(token, 'clicked');
      console.log('✅ Successfully tracked Google review click');
    
      // Try to open in new tab
      console.log('🌐 Attempting to open Google review URL...');
      const newWindow = window.open(reviewUrl, '_blank', 'noopener,noreferrer');
      
      // Check if popup was blocked
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        console.warn('⚠️ Popup blocked, trying alternative method');
        
        // Fallback: try direct navigation if popup blocked
        if (confirm('A popup blocker may be preventing the Google review page from opening. Click OK to open it now.')) {
          window.location.href = reviewUrl;
        }
      } else {
        console.log('✅ Successfully opened Google review URL in new tab');
        
        // Focus the new window if possible
        try {
          newWindow.focus();
        } catch (focusError) {
          console.warn('⚠️ Could not focus new window:', focusError);
        }
      }
    } catch (error) {
      console.error('❌ Error opening Google review URL:', error);
      alert(`Error opening Google review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getCurrentField = (): FeedbackFormField | null => {
    return formFields[currentStep - 1] || null;
  };

  const handleRatingClick = (rating: number) => {
    const currentField = getCurrentField();
    if (!currentField || currentField.field_type !== 'rating') return;

    setResponses(prev => ({
      ...prev,
      [currentField.field_key]: rating
    }));

    // Auto-advance to next step after a short delay for rating fields
    setTimeout(() => {
      if (currentStep < formFields.length) {
        setCurrentStep(currentStep + 1);
      }
    }, 500);
  };

  const handleTextChange = (value: string) => {
    const currentField = getCurrentField();
    if (!currentField || currentField.field_type !== 'text') return;

    setResponses(prev => ({
      ...prev,
      [currentField.field_key]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < formFields.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateResponses = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    for (const field of formFields) {
      const value = responses[field.field_key];

      // Check required fields
      if (field.is_required && (value === undefined || value === null || value === '' || value === 0)) {
        errors.push(`${field.field_label} is required`);
        continue;
      }

      // Skip validation if field is not provided and not required
      if (value === undefined || value === null || value === '' || value === 0) {
        continue;
      }

      // Validate rating fields
      if (field.field_type === 'rating') {
        const numValue = Number(value);
        if (isNaN(numValue) || numValue < 1 || numValue > 5) {
          errors.push(`${field.field_label} must be a rating between 1 and 5`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleSubmit = async () => {
    if (!token) {
      setError('Invalid feedback token');
      return;
    }

    const validation = validateResponses();
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const success = await submitDynamicFeedback(token, responses);
      
      if (success) {
        setSubmitted(true);
        
        // Check if customer is eligible for Google review request
        const isEligible = checkGoogleReviewEligibility();
        if (isEligible && !googleReviewTracked) {
          setShowGoogleReview(true);
          // Track that Google review was prompted
          await updateGoogleReviewTracking(token, 'prompted');
          setGoogleReviewTracked(true);
        }
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ 
    rating, 
    onStarClick, 
    disabled = false,
    size = "large"
  }: { 
    rating: number; 
    onStarClick: (rating: number) => void; 
    disabled?: boolean;
    size?: "small" | "large";
  }) => {
    const starSize = size === "large" ? "h-12 w-12" : "h-8 w-8";
    
    return (
      <div className="flex justify-center space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onStarClick(star)}
            className={`p-2 transition-all duration-200 ${
              disabled ? 'cursor-not-allowed' : 'hover:scale-110 transform cursor-pointer'
            }`}
          >
            <Star
              className={`${starSize} transition-colors duration-200 ${
                star <= rating
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300 hover:text-yellow-200'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const StepIndicator = () => (
    <div className="flex justify-center mb-8">
      <div className="flex space-x-4">
        {formFields.map((_, index) => (
          <div
            key={index}
            className={`w-3 h-3 rounded-full transition-colors duration-300 ${
              index + 1 <= currentStep
                ? 'bg-white'
                : 'bg-white bg-opacity-30'
            }`}
          />
        ))}
      </div>
    </div>
  );

  // Helper function to safely format dates
  const formatDate = (dateString: string | undefined, formatString: string): string => {
    if (!dateString) return 'Not specified';
    
    try {
      // Try parsing as ISO string first
      let date = parseISO(dateString);
      
      // If that fails, try creating a new Date
      if (!isValid(date)) {
        date = new Date(dateString);
      }
      
      // If still invalid, return fallback
      if (!isValid(date)) {
        return 'Not specified';
      }
      
      return format(date, formatString);
    } catch (error) {
      console.warn('Date formatting error:', error, 'for date:', dateString);
      return 'Not specified';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading feedback form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !feedback) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Feedback Unavailable</h1>
            <p className="text-gray-600 mb-6">
              {error || 'This feedback link is invalid or has expired.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
            <p className="text-gray-600 mb-6">
              Your feedback has been submitted successfully. We appreciate you taking the time to help us improve our service.
            </p>
            
            {/* Google Review Request */}
            {showGoogleReview && googleReviewSettings?.google_review_url && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <ExternalLink className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">🎉 Thanks for the great feedback!</h3>
                </div>
                <p className="text-gray-700 mb-4 text-sm">
                  Would you mind sharing your experience on Google to help other customers find us?
                </p>
                <button
                  onClick={handleGoogleReviewClick}
                  className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors shadow-sm"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Leave Google Review
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  This will open Google in a new tab
                </p>
              </div>
            )}
            
            {/* Show submitted responses */}
            {formFields.length > 0 && (
              <div className="text-sm text-gray-500 mb-4 space-y-2">
                {formFields.map((field) => {
                  const value = responses[field.field_key];
                  if (!value && value !== 0) return null;
                  
                  return (
                    <div key={field.id} className="flex justify-between items-center">
                      <span className="font-medium">{field.field_label}:</span>
                      <span>
                        {field.field_type === 'rating' 
                          ? `${value}/5 ⭐` 
                          : String(value).length > 30 
                            ? `${String(value).substring(0, 30)}...` 
                            : String(value)
                        }
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const booking = feedback.booking;
  const resources = booking?.resources || [];
  const currentField = getCurrentField();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 text-center">
            {/* Resource Details */}
            <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-4">
              {resources.length > 0 ? (
                <div className="flex flex-col items-center space-y-3">
                  {/* Multiple Resources or Single Resource */}
                  {resources.length === 1 ? (
                    // Single Resource Display
                    <>
                      <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center overflow-hidden">
                        {resources[0].resource?.avatar_url ? (
                          <img
                            src={resources[0].resource.avatar_url}
                            alt={`${resources[0].resource.first_name} ${resources[0].resource.last_name}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-white">
                            {resources[0].resource ? 
                              `${resources[0].resource.first_name?.charAt(0) || ''}${resources[0].resource.last_name?.charAt(0) || ''}` : 
                              'S'
                            }
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <h2 className="text-lg font-semibold">
                          {resources[0].resource ? 
                            `${resources[0].resource.first_name} ${resources[0].resource.last_name}` : 
                            'Your Service Provider'
                          }
                        </h2>
                      </div>
                    </>
                  ) : (
                    // Multiple Resources Display
                    <>
                      <div className="flex flex-wrap justify-center gap-2 max-w-xs">
                        {resources.map((resourceItem, index) => (
                          <div
                            key={resourceItem.resource?.id || index}
                            className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center overflow-hidden border-2 border-white border-opacity-50"
                          >
                            {resourceItem.resource?.avatar_url ? (
                              <img
                                src={resourceItem.resource.avatar_url}
                                alt={`${resourceItem.resource.first_name} ${resourceItem.resource.last_name}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="text-sm font-semibold text-white">
                                {resourceItem.resource ? 
                                  `${resourceItem.resource.first_name?.charAt(0) || ''}${resourceItem.resource.last_name?.charAt(0) || ''}` : 
                                  'S'
                                }
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      <div className="text-center">
                        <h2 className="text-lg font-semibold">
                          Your Service Team
                        </h2>
                        <div className="text-sm text-white text-opacity-80 mt-1">
                          {resources.length === 2 ? (
                            `${resources[0].resource?.first_name} ${resources[0].resource?.last_name} & ${resources[1].resource?.first_name} ${resources[1].resource?.last_name}`
                          ) : resources.length === 3 ? (
                            `${resources[0].resource?.first_name}, ${resources[1].resource?.first_name} & ${resources[2].resource?.first_name}`
                          ) : resources.length === 4 ? (
                            `${resources[0].resource?.first_name}, ${resources[1].resource?.first_name}, ${resources[2].resource?.first_name} & ${resources[3].resource?.first_name}`
                          ) : resources.length > 4 ? (
                            // For more than 4, show first 2 and count
                            `${resources[0].resource?.first_name}, ${resources[1].resource?.first_name} and ${resources.length - 2} team members`
                          ) : (
                            `${resources.length} service providers`
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Service Date and Time - only show if not "Not specified" */}
                  {(booking?.start_time && formatDate(booking.start_time, 'MMM d, yyyy') !== 'Not specified') && (
                    <div className="flex items-center space-x-3 text-xs text-white text-opacity-90">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(booking.start_time, 'MMM d, yyyy')}</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(booking.start_time, 'h:mm a')}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-3">
                  {/* Default Service Icon */}
                  <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  
                  <div>
                    <h2 className="text-lg font-semibold">
                      {booking?.title || 'Your Service'}
                    </h2>
                  </div>
                  
                  {/* Service Date and Time - only show if not "Not specified" */}
                  {(booking?.start_time && formatDate(booking.start_time, 'MMM d, yyyy') !== 'Not specified') && (
                    <div className="flex items-center space-x-3 text-xs text-white text-opacity-90">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(booking.start_time, 'MMM d, yyyy')}</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(booking.start_time, 'h:mm a')}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <StepIndicator />
          </div>

          {/* Question Content */}
          <div className="p-8">
            <div className="text-center min-h-[400px] flex flex-col justify-center">
              
              {currentField && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                      {currentField.field_label}
                    </h3>
                    <p className="text-gray-600 mb-8">
                      {currentField.field_type === 'rating' 
                        ? 'Please rate using the stars below' 
                        : 'Please share your thoughts'
                      }
                      {currentField.is_required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </p>
                  </div>

                  {/* Rating Field */}
                  {currentField.field_type === 'rating' && (
                    <StarRating
                      rating={responses[currentField.field_key] as number || 0}
                      onStarClick={handleRatingClick}
                      disabled={submitting}
                      size="large"
                    />
                  )}

                  {/* Text Field */}
                  {currentField.field_type === 'text' && (
                    <div className="max-w-lg mx-auto">
                      <textarea
                        value={responses[currentField.field_key] as string || ''}
                        onChange={(e) => handleTextChange(e.target.value)}
                        disabled={submitting}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-center"
                        placeholder={`Please share your ${currentField.field_label.toLowerCase()}...`}
                        required={currentField.is_required}
                      />
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex justify-center space-x-4">
                    {/* Back button (except for first step) */}
                    {currentStep > 1 && (
                      <button
                        onClick={handlePrevious}
                        disabled={submitting}
                        className="inline-flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Back</span>
                      </button>
                    )}

                    {/* Next/Submit button */}
                    {currentStep < formFields.length ? (
                      // Next button for text fields (rating fields auto-advance)
                      currentField.field_type === 'text' && (
                        <button
                          onClick={handleNext}
                          disabled={submitting}
                          className="inline-flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          <span>Next</span>
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      )
                    ) : (
                      // Submit button on last step
                      <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="inline-flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <>
                            <span>Submit Feedback</span>
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Progress indicator */}
                  <div className="text-sm text-gray-500 pt-4">
                    <p>Question {currentStep} of {formFields.length}</p>
                  </div>
                </div>
              )}

              {/* No fields configured */}
              {!currentField && formFields.length === 0 && !loading && (
                <div className="text-center text-gray-500">
                  <p>No feedback questions have been configured for this service.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 